var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import widgetsDictionary from "@/components/widgets/widgetsDictionary";
import { useParams } from "react-router-dom";
import { useGetCurrentUserQuery, useGetModuleFieldsBuilderQuery, useGetModuleListQuery, } from "@/store/api";
import { Select, Option, Typography } from "@mui/joy";
import DisplayField from "@/components/fields/displayField";
import { FieldViewType } from "@/components/fields/viewType";
import { useGenerateDistinctColors } from "@/pages/administration/colorGenerator";
import { useAppSelector } from "@/hooks/store";
/**
 * <b>Composant widget sélectionnable qui rend le tableau de bord d'un module,
 * affichant les informations par champ choisi du module dans un format de widget interactif.</b>
 */
export default function DashboardWidget(_a) {
    var _b, _c;
    var widget = _a.widget;
    var field = (_b = widget === null || widget === void 0 ? void 0 : widget.payload) === null || _b === void 0 ? void 0 : _b.field;
    var module = (_c = widget === null || widget === void 0 ? void 0 : widget.payload) === null || _c === void 0 ? void 0 : _c.module;
    var moduleList = useGetModuleListQuery({
        table: module,
        params: {},
    }).data;
    var _d = useAppSelector(function (state) { return state.theme; }), colorBase = _d.colorBase, midColor = _d.midColor, endColor = _d.endColor;
    var _e = useState([]), records = _e[0], setRecord = _e[1];
    useEffect(function () {
        var _a;
        moduleList && setRecord(((_a = moduleList === null || moduleList === void 0 ? void 0 : moduleList.data) === null || _a === void 0 ? void 0 : _a.records) || []);
    }, [moduleList]);
    function createObjectFromFieldAndValue(field, value) {
        var result = {};
        result[field] = value;
        return result;
    }
    function groupByField(records, field) {
        var grouped = {};
        records.forEach(function (record) {
            var fieldValue = record[field.name];
            if (fieldValue === undefined) {
                fieldValue = "";
            }
            if (!grouped[fieldValue]) {
                grouped[fieldValue] = 0;
            }
            grouped[fieldValue]++;
        });
        return Object.entries(grouped).map(function (_a) {
            var value = _a[0], count = _a[1];
            return ({ value: value, count: count });
        });
    }
    var useResponsiveFontSize = function (text, containerRef) {
        var _a = useState(12), fontSize = _a[0], setFontSize = _a[1];
        useEffect(function () {
            var adjustFontSize = function () {
                if (containerRef.current) {
                    var container = containerRef.current;
                    var maxHeight = container.offsetHeight;
                    var newSize = 12;
                    var textHeight = void 0;
                    container.style.fontSize = "".concat(newSize, "px");
                    textHeight = container.scrollHeight;
                    while (textHeight > maxHeight && newSize > 0) {
                        newSize -= 1;
                        container.style.fontSize = "".concat(newSize, "px");
                        textHeight = container.scrollHeight;
                    }
                    setFontSize(newSize);
                }
            };
            adjustFontSize();
            window.addEventListener("resize", adjustFontSize);
            return function () { return window.removeEventListener("resize", adjustFontSize); };
        }, [text, containerRef]);
        return fontSize;
    };
    var boolConvert = function (value) {
        if (value === "0" ||
            value === "false" ||
            value === false ||
            value === "False") {
            return "Not";
        }
        else {
            return "";
        }
    };
    var groupedByValue = groupByField(records, field);
    var numColors = "15";
    var shade = "400";
    var algo = "goldenRatio";
    var colors = useGenerateDistinctColors({
        numColors: numColors,
        shade: shade,
        colorBase: colorBase,
        algo: algo,
        endColor: endColor,
        midColor: midColor,
    });
    var valueToColor = colors &&
        Object.fromEntries(groupedByValue.map(function (group, index) { return [group.value, colors[index]]; }));
    var DataDisplay = function (_a) {
        var fieldData = _a.fieldData;
        var backgroundColor = (valueToColor && valueToColor[fieldData.value]) || "gray";
        var containerRef = useRef(null);
        var fontSize = useResponsiveFontSize(fieldData.value, containerRef);
        var rec = createObjectFromFieldAndValue(field.name, fieldData.value);
        return (_jsx(_Fragment, { children: _jsxs("div", __assign({ className: "flex flex-col items-center justify-center border-2 rounded-md p-4 m-1 w-auto", style: {
                    backgroundColor: backgroundColor,
                    //color: textColor,
                    color: "white",
                    minWidth: "150px",
                    maxWidth: "auto",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    overflow: "hidden",
                } }, { children: [_jsx("div", __assign({ className: "flex justify-center items-center flex-1 text-4xl", style: {
                            height: "40px",
                            overflow: "hidden",
                            marginBottom: "10px",
                        } }, { children: fieldData.count })), _jsx("div", __assign({ ref: containerRef, className: "flex-1 text-center text-black", style: {
                            fontSize: "".concat(fontSize, "px"),
                            lineHeight: 1.2,
                            display: "flex",
                            flexWrap: "wrap",
                            justifyContent: "center",
                            alignItems: "center",
                            textAlign: "center",
                            width: "auto",
                            minWidth: "80px",
                            whiteSpace: "nowrap",
                            overflow: "visible",
                        } }, { children: field.type === "Bool" ? (_jsxs(_Fragment, { children: [boolConvert(fieldData.value), " ", field.name] })) : (_jsx(DisplayField, { record: rec, moduleName: module, fieldName: field.name, viewType: FieldViewType.DETAIL, fieldMetadata: field, showEditButton: false, className: "text-white" })) }))] })) }));
    };
    return (_jsx("div", __assign({ className: "flex overflow-x-auto p-2 space-x-4" }, { children: groupedByValue.map(function (field, index) { return (_jsx(_Fragment, { children: _jsx(DataDisplay, { fieldData: field }, index) })); }) })));
}
function DashboardSettings(_a) {
    var _b, _c, _d;
    var widget = _a.widget, onChange = _a.onChange;
    var moduleName = useParams().moduleName;
    var _e = useState(), fieldDefs = _e[0], setFieldDefs = _e[1];
    var userPreferences = useGetCurrentUserQuery(null).data;
    var languagePreference = (_c = (_b = userPreferences === null || userPreferences === void 0 ? void 0 : userPreferences.data) === null || _b === void 0 ? void 0 : _b.preferences) === null || _c === void 0 ? void 0 : _c.language;
    var _f = useGetModuleFieldsBuilderQuery({
        params: {
            package: "",
            module: moduleName || "Contacts",
            lang: languagePreference || "en_us",
        },
    }), listFieldDefs = _f.data, error = _f.error, isLoading = _f.isLoading, isFetching = _f.isFetching, refetch = _f.refetch;
    useEffect(function () {
        if (listFieldDefs) {
            setFieldDefs(listFieldDefs.data.fieldsData);
        }
    }, [listFieldDefs]);
    return (_jsxs("div", __assign({ className: "", id: "testwidgetdashboard" }, { children: [_jsx("label", __assign({ htmlFor: "content-type-button", id: "content-type-label" }, { children: "Field" })), _jsx(Select, __assign({ value: ((_d = widget === null || widget === void 0 ? void 0 : widget.payload) === null || _d === void 0 ? void 0 : _d.field) || "", onChange: function (event, v) {
                    onChange &&
                        onChange(widget, __assign(__assign({}, widget.payload), { module: moduleName || "Contacts", field: v }));
                } }, { children: fieldDefs &&
                    (fieldDefs === null || fieldDefs === void 0 ? void 0 : fieldDefs.map(function (field, index) { return (_jsx(Option, __assign({ value: field }, { children: _jsx(Typography, { children: field.name }) }), index)); })) }))] })));
}
widgetsDictionary.register("widget/dashboard", {
    name: "Dashboard",
    validation: true,
    display: DashboardWidget,
    settings: DashboardSettings,
});
